import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  Modal,
  Slide,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import { colors } from "../../common/colors";
import {
  convertUserImage,
  getRole,
  isGroupLeader,
  isMemberRole,
  isSubLeaderRole
} from "../../util/utils";
import ButtonOutlinedCentered from "../ButtonOutlined";
import axios, { CancelTokenSource } from "axios";
import appServices from "../../services/app-services/appServices";
import { useSelector } from "react-redux";
import { PrivateUser, UserEntity } from "../../util/CreatePost/types";
import { T } from "react-translator-component";
import { isSamePrivateUser } from "../../util/CreatePost/utils";

interface Props {
  groupInfo: any;
  allMembers: any[];
  onAdd: (user: any) => void;
  onDelete: (user: UserEntity) => void;
}

export default function SelectMembers({ groupInfo, allMembers, onAdd, onDelete }: Props) {
  const { user } = useSelector((state: any) => state.userAuth);
  const post = useSelector((state: any) => state.createPost.values);

  const [filteredMembers, setFilteredMembers] = useState<any>([]);
  const [showMembers, setShowMembers] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [userSearchLoading, setUserSearchLoading] = useState<boolean>(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const carouselRef = useRef<Carousel>(null);

  const handleSearch = async (value: string) => {
    setUserSearchLoading(true)
    setSearchTerm(value);
    if (!value) {
      setFilteredMembers([])
      setUserSearchLoading(false)
      return
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    await searchUsers(value, cancelTokenRef.current.token)
    setUserSearchLoading(false)
  };

  const searchUsers = async (term: string, token: any) => {
    const cleanedTerm = term.replace(/^[\s\u3000]+/, '');

    try {
      const response = await appServices.searchUsers(cleanedTerm, groupInfo.id, token);
      const data = response.users

      setFilteredMembers(data.filter((member: any) => member.id !== user.id));
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    }
  };

  function isMemberAdded(groupUser: any) {
    return post.privateUsers.some(
      (member: PrivateUser) =>
        member._destroy === 0 && isSamePrivateUser(member, groupUser.class_name, groupUser.id)
    );
  }

  const displayRole = (user: any) => {
    let role;
    if (isGroupLeader(groupInfo, user)) {
      role = "leader"
    } else {
      role = allMembers?.find((member: any) => user.id === member.user.id)?.role
    }

    if (!role || isMemberRole(role)) return ""

    return getRole(role)
  }

  const groupMemberRoleFilter = (isMember: boolean) => {
    const filteredGroupMember = allMembers.filter((member: any) => {
      return isMember ? isMemberRole(member.role) : !isMemberRole(member.role)
    })

    const fetchUsers = filteredGroupMember.filter((member: any) => {
      if (filteredMembers.some((filteredMember: any) => filteredMember.id === member.user.id)) return member
    })

    if (isMember) return fetchUsers

    return fetchUsers.sort((a: any, b: any) => {
      if (isGroupLeader(groupInfo, a) && !isGroupLeader(groupInfo, b)) {
        return -1
      } else if (!isGroupLeader(groupInfo, a) && isGroupLeader(groupInfo, b)) {
        return 1
      } else if (isSubLeaderRole(a.role) && !isSubLeaderRole(b.role)) {
        return -1
      } else if (!isSubLeaderRole(a.role) && isSubLeaderRole(b.role)) {
        return 1
      } else {
        return a.id - b.id
      }
    })
  }

  const displayMember = (member: any, index: number) => {
    const groupMember = member.user

    const handleDeleteMember = () => {
      if (carouselRef.current) {
        carouselRef.current.goToSlide(0);
      }

      const targetPrivateUser = post.privateUsers.find(
        (privateUser: PrivateUser) => isSamePrivateUser(privateUser, groupMember.class_name, groupMember.id)
      )
      onDelete(targetPrivateUser)
    }

    return (
      <Box sx={styles.memberContainer} key={index}>
        <Avatar src={convertUserImage(groupMember.image)} sx={ styles.searchedUserAvatar } />
        <Box sx={ styles.searchedUserName }>
          <Typography>{groupMember?.nickname}</Typography>
        </Box>
        <Typography color="text.secondary">
          {displayRole(groupMember)}
        </Typography>
        {!isMemberAdded(groupMember) ? (
          <AddCircleOutlineIcon onClick={() => onAdd(groupMember)} sx={ styles.userAddIcon } />
        ) : (
          <RemoveCircleOutlineIcon onClick={() => handleDeleteMember()} sx={ styles.userRemoveIcon } />
        )}
      </Box>
    )
  }

  return (
    <>
      <Box>
        <Carousel
          responsive={{
            superLargeDesktop: {
              breakpoint: { max: 4000, min: 1024 },
              items: 5,
            },
            desktop: { breakpoint: { max: 1024, min: 768 }, items: 3 },
            tablet: { breakpoint: { max: 768, min: 464 }, items: 2 },

            mobile: {
              breakpoint: { max: 464, min: 0 },
              items: 2,
            },
          }}
          swipeable={true}
          draggable={true}
          showDots={false}
          arrows={true}
          infinite={false}
          keyBoardControl={true}
          itemClass="carousel-item-padding"
          ref={carouselRef}
        >
          {post.privateUsers
            .filter((_privateUser: PrivateUser) => _privateUser._destroy === 0)
            .slice().reverse()
            .map((privateUser: PrivateUser, index: number) => (
              <Box key={index} sx={ styles.userInfo }>
                <Avatar
                  sx={ styles.userAvatar }
                  alt={privateUser?.nickname?.slice(0, 1)}
                  src={convertUserImage(privateUser?.image)}
                />
                <Typography sx={ styles.userName }>{privateUser?.nickname}</Typography>
                {!isSamePrivateUser(privateUser, post.creator_type, post.creator_id) &&
                  <RemoveCircleOutlineIcon
                    onClick={() => onDelete(privateUser)}
                    sx={ styles.removeCircleIcon }
                  />
                }
              </Box>
            ))
          }
        </Carousel>
        <ButtonOutlinedCentered
          sx={ styles.m10 }
          onClick={() => setShowMembers(true)}
          title={T("select_users")}
        />
      </Box>

      <Modal open={showMembers} onClose={() => setShowMembers(false)}>
        <Slide direction="up" in={showMembers} mountOnEnter unmountOnExit>
          <div style={styles.modalContainer}>
            <Box sx={styles.headerStyle}>
              <Box onClick={() => setShowMembers(false)} sx={styles.backButtonStyle}>
                <Typography>{T("back_kanji")}</Typography>
                <KeyboardArrowDownIcon />
              </Box>
            </Box>
            <Box sx={styles.searchBoxStyle}>
              <Typography sx={styles.instruction}>{T("can_search_for_users")}</Typography>
              <TextField
                sx={styles.textField}
                id="outlined-basic"
                label={T("search")}
                placeholder={`${T("search")}`}
                variant="outlined"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  endAdornment:
                    <InputAdornment
                      position="end"
                    >
                      {userSearchLoading && <CircularProgress size={20} />}
                    </InputAdornment>,
                }}
              />
            </Box>
            {groupMemberRoleFilter(false).length > 0 &&
              <div style={styles.representative}>
                <Typography sx={{ textAlign: "left", marginLeft: "15px" }}>{T("members_with_roles")}</Typography>
                {groupMemberRoleFilter(false).map((member: any, index: number) => (
                  displayMember(member, index)
                ))}
              </div>
            }

            {groupMemberRoleFilter(true).length > 0 &&
              <div>
                <Typography sx={{ textAlign: "left", marginLeft: "15px" }}>{T("member")}</Typography>
                {groupMemberRoleFilter(true).map((member: any, index: number) => (
                  displayMember(member, index)
                ))}
              </div>
            }
          </div>
        </Slide>
      </Modal>
    </>
  )
};

const styles = {
  removeCircleIcon: {
    fontSize: "30px",
    cursor: "pointer",
    color: colors.farm_red600
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    backgroundColor: colors.farm_white,
    padding: "5px 10px",
    borderRadius: "5px"
  },
  userAvatar: {
    width: "24px",
    height: "24px"
  },
  userName: {
    margin: "5px",
    flex: 1
  },
  m10: {
    margin: "10px"
  },

  searchBoxStyle: {
    padding: "10px 0",
    width: "100%",
    marginTop: "10px"
  },
  textField: {
    marginTop: "15px",
    background: colors.farm_white,
    width: "100%"
  },
  instruction: {
    mb: 1
  },
  headerStyle: {
    textAlign: "center",
    marginBottom: "10px"
  },
  backButtonStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer"
  },
  modalContainer: {
    width: "100%",
    height: "100%",
    overflow: "scroll",
    backgroundColor: colors.farm_white,
    padding: 20,
  },
  memberContainer: {
    flex: 1,
    display: "flex",
    alingItems: "center",
    padding: "10px 0",
    borderBottom: `1px solid ${colors.farm_gray100}`,
  },
  searchedUserAvatar: {
    height: "26px",
    width: "26px"
  },
  searchedUserName: {
    margin: "0px 10px",
    flex: 1
  },
  userAddIcon: {
    fontSize: "30px",
    cursor: "pointer",
    color: colors.farm_green500
  },
  userRemoveIcon: {
    fontSize: "30px",
    cursor: "pointer",
    color: colors.farm_red600
  },
  representative: {
    margin: "0 0 50px 0"
  }
};
